import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Terminal, Search, Send, Users, Cpu, Clock, Battery, Wallet,
  Power, PowerOff, RefreshCw, Zap,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import StatusPill from "@/components/StatusPill";

/**
 * Every entry:
 *   value:  command_str the backend expects
 *   label:  human title
 *   desc:   one-line explanation
 *   icon:   lucide icon
 *   category: "query" | "control" | "config"
 *   params: array of param inputs { key, label, type, default, options?, help?, unit?, min?, max?, placeholder? }
 */
const COMMANDS = [
  { value: "queryFlowAndStatus", label: "Query flow & status", desc: "Force an immediate PAFDS uplink (flow, temperature, valve, alarms, battery, signal).", icon: RefreshCw, category: "query", params: [] },
  { value: "queryBalance",       label: "Query balance",       desc: "Ask the meter to report its current credit balance (PBAL).",                                icon: Wallet,    category: "query", params: [] },
  { value: "queryBattery",       label: "Query battery",       desc: "Alias of Query flow & status — battery voltage & signal are included in every PAFDS.",      icon: Battery,   category: "query", params: [] },

  { value: "openValve",   label: "Open valve",  desc: "Enable gas flow. Requires meter to have credit.",  icon: Power,    category: "control", params: [] },
  { value: "closeValve",  label: "Close valve", desc: "Shut off gas flow immediately.",                    icon: PowerOff, category: "control", params: [] },
  { value: "remotelyTopUp", label: "Remote top-up (m³)", desc: "Advanced — credit or debit gas volume directly by cubic meters (bypasses UGX pricing).",
    icon: Wallet, category: "control",
    params: [{
      key: "gas_m3", label: "Gas volume", type: "number", default: 1.0, unit: "m³",
      min: -100, max: 100, placeholder: "1.0",
      help: "Positive = top-up, negative = deduct. Range −100 to +100 m³. Same as Payments → Top-up but skips the UGX ↔ m³ conversion.",
    }] },

  { value: "setReportInterval", label: "Set report interval", desc: "How often the meter uploads PAFDS. Enter as HH:MM:SS.",
    icon: Clock, category: "config",
    params: [
      {
        key: "hours", label: "Hours", type: "number", default: 0, unit: "hh",
        min: 0, max: 23, placeholder: "0",
        help: "0–23. Leave at 0 if you only need minutes.",
      },
      {
        key: "minutes", label: "Minutes", type: "number", default: 13, unit: "mm",
        min: 0, max: 59, placeholder: "13",
        help: "0–59. Common: 5 (default), 13, 15 (production), 30, 60 (=1 h).",
      },
      {
        key: "seconds", label: "Seconds", type: "number", default: 0, unit: "ss",
        min: 0, max: 59, placeholder: "0",
        help: "0–59. This firmware supports minute granularity — any non-zero seconds bump the interval up by one minute.",
      },
    ] },
  { value: "setClock", label: "Set clock", desc: "Push the current UTC time to the meter RTC.",
    icon: Clock, category: "config",
    params: [{
      key: "iso", label: "UTC timestamp", type: "text", default: "", placeholder: "2026-07-25T11:30:00Z",
      help: "ISO-8601 UTC string. Leave blank to auto-fill with server 'now' at send time.",
    }] },
  { value: "setPrepaidMode", label: "Set prepaid mode", desc: "Toggle between prepaid (credit-gated) and postpaid.",
    icon: Zap, category: "config",
    params: [{
      key: "enabled", label: "Billing mode", type: "select", default: "true",
      options: [{ v: "true", l: "Prepaid (valve closes on zero balance)" }, { v: "false", l: "Postpaid (invoice-based)" }],
      help: "Prepaid meters auto-close their valve when balance ≤ 0. Postpaid meters keep the valve open regardless.",
    }] },

  { value: "setRemoteServer", label: "Set remote server", desc: "Repoint the meter at a different UDP bridge (droplet ↔ AWS cutover).",
    icon: Zap, category: "config",
    params: [
      {
        key: "ip", label: "Bridge IP", type: "text", default: "3.120.80.240", placeholder: "3.120.80.240",
        help: "Public IPv4 of the UDP bridge (AWS Elastic IP or DO reserved IP). Hostnames also work if DNS is available on the modem.",
      },
      {
        key: "port", label: "Port", type: "number", default: 5683, unit: "udp",
        min: 1, max: 65535, placeholder: "5683",
        help: "UDP port the bridge listens on (default 5683).",
      },
    ] },

  { value: "rawDownlink", label: "Raw ZYMSG downlink", desc: "Advanced — send raw CMD+ARG hex payload. No CRC, no IMEI.",
    icon: Terminal, category: "config",
    params: [{
      key: "hex", label: "Hex payload", type: "text", default: "", placeholder: "0B",
      help: 'Format: CMD (1 byte) + ARG (N bytes). No IMEI prefix, no CRC (both stripped by the meter). Examples: "0B" query flow · "0C" + amount_le32 recharge · "0A01" open valve.',
    }] },
];

const groupLabels = { query: "Query commands", control: "Valve control", config: "Configuration" };

export default function AtCommands({ embedded = false }) {
  const [meters, setMeters] = useState([]);
  const [selectedMeters, setSelectedMeters] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCmd, setSelectedCmd] = useState(COMMANDS[0]);
  const [params, setParams] = useState({});
  const [onlyOnline, setOnlyOnline] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    try {
      const [m, h] = await Promise.all([
        api.get("/meters", { params: { page_size: 1000 } }),
        api.get("/households", { params: { page_size: 1000 } }),
      ]);
      const houseById = Object.fromEntries((h.data.data || []).map(x => [x.id, x.household_name]));
      setMeters((m.data.data || []).map(x => ({ ...x, _hname: x.household_id ? houseById[x.household_id] : null })));
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Failed to load meters");
    }
  };
  useEffect(() => { load(); }, []);

  // Reset param defaults whenever the command changes
  useEffect(() => {
    const p = {};
    (selectedCmd.params || []).forEach(x => { p[x.key] = x.default; });
    setParams(p);
  }, [selectedCmd]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return meters;
    return meters.filter(m =>
      (m.serial_number || "").toLowerCase().includes(q) ||
      (m.imei || "").toLowerCase().includes(q) ||
      (m.deveui || "").toLowerCase().includes(q) ||
      (m._hname || "").toLowerCase().includes(q),
    );
  }, [search, meters]);

  const toggleMeter = (id) => {
    setSelectedMeters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      setSelectAll(false);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectAll) {
      setSelectAll(false);
      setSelectedMeters(new Set());
    } else {
      setSelectAll(true);
      setSelectedMeters(new Set());       // fleet-wide mode; individual set is irrelevant
    }
  };

  const targetCount = selectAll ? meters.length : selectedMeters.size;

  const submit = async () => {
    if (!selectAll && selectedMeters.size === 0) {
      toast.error("Select one or more meters (or Select all)");
      return;
    }
    setSending(true); setLastResult(null);
    try {
      // Coerce param types
      const command_params = {};
      (selectedCmd.params || []).forEach(p => {
        let v = params[p.key];
        if (p.type === "number") v = Number(v);
        if (p.type === "select" && v === "true") v = true;
        else if (p.type === "select" && v === "false") v = false;
        if (v !== undefined && v !== "") command_params[p.key] = v;
      });
      // setClock convenience — server 'now' if blank
      if (selectedCmd.value === "setClock" && !command_params.iso) {
        command_params.iso = new Date().toISOString();
      }
      const body = {
        command_str: selectedCmd.value,
        command_params,
        meter_ids: selectAll ? null : Array.from(selectedMeters),
        only_online: onlyOnline,
      };
      const { data } = await api.post("/commands/broadcast", body);
      setLastResult({ ok: true, ...data });
      toast.success(`${selectedCmd.label} queued for ${data.queued} meter${data.queued === 1 ? "" : "s"}`);
    } catch (e) {
      const msg = formatDetail(e.response?.data?.detail) || "Failed to queue command";
      setLastResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const grouped = COMMANDS.reduce((acc, c) => {
    (acc[c.category] ||= []).push(c);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="at-commands-page">
      {!embedded && (
        <>
          <div className="flex items-center gap-3">
            <Link to="/configurations" className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 text-sm" data-testid="at-back-link">
              <ArrowLeft size={14} /> Configurations
            </Link>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">System · Meter Ops</div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">AT Commands</h1>
            <p className="text-sm text-slate-500 mt-1">
              Queue server-side commands to one meter, a subset, or the entire fleet.
              Downlinks are delivered on each meter&apos;s next uplink cycle.
            </p>
          </div>
        </>
      )}

      <div className="grid gap-6 lg:grid-cols-5">
        {/* LEFT — command picker */}
        <div className="lg:col-span-2 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">1 · Command</div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="at-command-list">
            {Object.keys(grouped).map((cat) => (
              <div key={cat}>
                <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-widest text-slate-400 font-semibold bg-slate-50 border-b border-slate-100">
                  {groupLabels[cat] || cat}
                </div>
                {grouped[cat].map((c) => {
                  const Icon = c.icon;
                  const active = selectedCmd.value === c.value;
                  return (
                    <button
                      key={c.value} type="button"
                      onClick={() => setSelectedCmd(c)}
                      data-testid={`at-cmd-${c.value}`}
                      className={`w-full flex items-start gap-3 px-3 py-2.5 text-left border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${active ? "bg-brand-50" : ""}`}
                    >
                      <div className={`h-8 w-8 shrink-0 rounded-sm grid place-items-center border ${active ? "bg-brand-600 text-white border-brand-600" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                        <Icon size={14} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm font-semibold ${active ? "text-brand-700" : "text-slate-800"}`}>{c.label}</div>
                        <div className="text-[11px] text-slate-500 leading-relaxed">{c.desc}</div>
                        <div className="text-[10px] font-mono text-slate-400 mt-0.5">{c.value}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Parameters — ALWAYS visible so operators see the exact format */}
          <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-3" data-testid="at-command-params">
            <div className="flex items-baseline justify-between">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">3 · Parameters</div>
              <div className="text-[10px] font-mono text-slate-400">{selectedCmd.value}</div>
            </div>
            {(selectedCmd.params || []).length === 0 ? (
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                <span className="font-semibold text-slate-700">No parameters required.</span>
                {" "}This command sends only the command byte to the meter.
              </div>
            ) : (
              <div className="space-y-4">
                {selectedCmd.params.map((p) => (
                  <div key={p.key} data-testid={`at-param-block-${p.key}`}>
                    <div className="flex items-baseline justify-between">
                      <Label className="text-xs text-slate-700 font-semibold">{p.label}</Label>
                      {p.unit && <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{p.unit}</span>}
                    </div>
                    {p.type === "select" ? (
                      <select
                        className="mt-1 h-10 w-full border border-slate-300 rounded-sm px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
                        value={params[p.key] ?? p.default}
                        onChange={(e) => setParams({ ...params, [p.key]: e.target.value })}
                        data-testid={`at-param-${p.key}`}
                      >
                        {p.options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                    ) : p.type === "number" ? (
                      <div className="mt-1 flex items-stretch border border-slate-300 rounded-sm bg-white focus-within:ring-2 focus-within:ring-brand-500/30 focus-within:border-brand-500">
                        <Input
                          type="number"
                          min={p.min}
                          max={p.max}
                          placeholder={p.placeholder}
                          value={params[p.key] ?? ""}
                          onChange={(e) => setParams({ ...params, [p.key]: e.target.value })}
                          data-testid={`at-param-${p.key}`}
                          className="border-0 rounded-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 h-10 tabular-nums"
                        />
                        {p.unit && (
                          <div className="flex items-center px-3 border-l border-slate-200 text-xs text-slate-500 bg-slate-50 select-none">
                            {p.unit}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Input
                        type={p.type} value={params[p.key] ?? ""}
                        placeholder={p.placeholder}
                        className="mt-1 rounded-sm font-mono"
                        onChange={(e) => setParams({ ...params, [p.key]: e.target.value })}
                        data-testid={`at-param-${p.key}`}
                      />
                    )}
                    {p.help && (
                      <div className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                        <span className="text-slate-400 font-semibold uppercase tracking-widest">Format:</span> {p.help}
                      </div>
                    )}
                    {p.type === "number" && (p.min !== undefined || p.max !== undefined) && (
                      <div className="text-[10px] text-slate-400 mt-1 font-mono">
                        Range: {p.min ?? "–∞"} to {p.max ?? "+∞"}{p.unit ? ` ${p.unit}` : ""}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Live payload preview */}
            <div className="border-t border-slate-100 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Preview payload</div>
              <div className="font-mono text-[11px] bg-slate-900 text-emerald-300 rounded-sm px-3 py-2 overflow-x-auto whitespace-pre" data-testid="at-payload-preview">
{JSON.stringify(
  {
    command_str: selectedCmd.value === "openValve" || selectedCmd.value === "closeValve"
      ? "setValveState"
      : selectedCmd.value,
    command_params: (() => {
      const out = {};
      (selectedCmd.params || []).forEach((p) => {
        let v = params[p.key];
        if (v === undefined || v === "") return;
        if (p.type === "number") v = Number(v);
        if (p.type === "select" && v === "true") v = true;
        else if (p.type === "select" && v === "false") v = false;
        out[p.key] = v;
      });
      if (selectedCmd.value === "openValve") out.valve_state = "OPEN";
      if (selectedCmd.value === "closeValve") out.valve_state = "CLOSED";
      return out;
    })(),
  },
  null, 2,
)}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT — meter selector */}
        <div className="lg:col-span-3 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">2 · Target meters</div>
            <div className="text-xs text-slate-500 tabular-nums" data-testid="at-target-count">
              {targetCount === 0 ? "No meters selected" : selectAll ? `All ${meters.length} meters` : `${targetCount} selected`}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="p-3 border-b border-slate-200 flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none">
                <Checkbox checked={selectAll} onCheckedChange={toggleAll} data-testid="at-select-all" />
                <Users size={14} className="text-slate-500" />
                Select all meters (fleet broadcast)
              </label>
              <div className="ml-auto flex items-center gap-2">
                <label className="inline-flex items-center gap-2 text-xs text-slate-500 select-none">
                  <Checkbox checked={onlyOnline} onCheckedChange={(v) => setOnlyOnline(!!v)} data-testid="at-only-online" />
                  Only meters seen in last 24 h
                </label>
              </div>
            </div>

            <div className="p-3 border-b border-slate-200">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by household, IMEI or meter number…"
                  className="pl-9 rounded-sm"
                  data-testid="at-meter-search"
                  disabled={selectAll}
                />
              </div>
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="text-center py-8 text-slate-400 text-sm">No meters match your search.</div>
              ) : filtered.map((m) => (
                <label
                  key={m.id}
                  data-testid={`at-meter-row-${m.id}`}
                  className={`flex items-center gap-3 px-4 py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${selectAll ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <Checkbox
                    checked={selectAll || selectedMeters.has(m.id)}
                    disabled={selectAll}
                    onCheckedChange={() => toggleMeter(m.id)}
                  />
                  <Cpu size={14} className="text-slate-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {m._hname || m.customer_name || <span className="italic text-slate-400">Unassigned</span>}
                    </div>
                    <div className="text-[11px] font-mono text-slate-500 truncate">
                      {m.serial_number}{m.imei ? ` · IMEI ${m.imei}` : m.deveui ? ` · DevEUI ${m.deveui}` : ""}
                    </div>
                  </div>
                  <StatusPill value={m.valve_state} />
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <div className="text-xs text-slate-500">
              Command: <span className="font-mono text-slate-800">{selectedCmd.value}</span>
              {" · "}Targets: <span className="font-semibold">{targetCount}</span>
            </div>
            <Button
              onClick={submit}
              disabled={sending || (!selectAll && selectedMeters.size === 0)}
              data-testid="at-send-button"
              className="bg-brand-600 hover:bg-brand-700 rounded-sm"
            >
              <Send size={14} className="mr-1" /> {sending ? "Queuing…" : "Send to meter(s)"}
            </Button>
          </div>

          {lastResult && (
            <div
              data-testid="at-result"
              className={`rounded-sm px-3 py-2 text-xs border ${lastResult.ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}
            >
              {lastResult.ok
                ? <>Queued <span className="font-semibold">{lastResult.queued}</span> command{lastResult.queued === 1 ? "" : "s"} — <span className="font-mono">{lastResult.command_str}</span>. Downlinks will be sent on each meter&apos;s next uplink.</>
                : <>Error: {lastResult.error}</>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
